// src/components/pages/Movimento/CreateOrEdit/modules/useAditivoModule.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useActionLock } from '../../../../../hooks/useActionLock';
import moment from 'moment';
import { relatorios } from '../../../../../api/relatorios'; // ✅ ajuste path conforme seu projeto

import { formatarNumeroDecimal } from '../../../../../utils/masks';
import { printPdf } from '../../../../../utils/funcoes';
import { empresa } from '../../../../../api/empresa';
// helper simples (usa pt-BR)
const parsePtbrNumber = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;

    const s = String(v).trim();
    if (!s) return null;

    // "1.234,56" -> 1234.56
    if (s.includes(',')) {
        const n = Number(s.replace(/\./g, '').replace(',', '.'));
        return Number.isFinite(n) ? n : null;
    }

    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

export const useAditivoModule = ({
    operacao,
    dados,

    MovimentoAPI,
    setFlashMessage,
    fetchData,
    mrt: mrtState,

    // base do valorAnterior
    getValorAnteriorBase, // () => number | null
}) => {
    const saveLock = useActionLock();
    const refreshLock = useActionLock();
    const printLock = useActionLock();

    const [itemAditivos, setItemAditivos] = useState([]);
    const [rowCountAditivo, setRowCountAditivo] = useState(0);
    const [isFetchingAditivos, setIsFetchingAditivos] = useState(false);
    const [isErrorAditivos, setIsErrorAditivos] = useState(false);

    const initialNovoAditivo = useMemo(
        () => ({
            uuid: null,
            empresaId: dados.empresaId,
            movimentoId: dados?.id,
            tipo: null,
            dataAditivo: null,
            valorAnterior: null,
            percentual: 0,
            valorReajustado: 0,
            quantidade: 0,
            observacao: null,
            ativo: true,
            funcao: 'Adicionar',
        }),
        [dados?.id]
    );

    const [novoAditivo, setNovoAditivo] = useState(initialNovoAditivo);

    useEffect(() => {
        setNovoAditivo((prev) => ({ ...prev, movimentoId: dados?.id }));
    }, [dados?.id]);

    useEffect(() => {
        if (novoAditivo.tipo && novoAditivo.funcao.toLocaleLowerCase() === 'adicionar') {
            // Verifica cada item em itemAditivos
            itemAditivos.forEach((item) => {
                // Comparar as datas e o tipo
                if (novoAditivo.dataAditivo <= item.data && novoAditivo.tipo === item.tipo) {
                    // Adicione a lógica que você deseja aqui
                    setFlashMessage(
                        `Último Termo Aditivo de ${novoAditivo.tipo} foi ${moment(item.data).format(
                            'DD/MM/YYYY'
                        )}, a data tem que ser superior ao último reajuste!`,
                        'warning'
                    );
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [novoAditivo.dataAditivo]);
    //funções
    const getAluguelAtual = useCallback(() => {
        const base = typeof getValorAnteriorBase === 'function' ? getValorAnteriorBase() : null;
        return base ?? null;
    }, [getValorAnteriorBase]);
    const limparAditivo = useCallback(() => {
        setNovoAditivo({
            ...initialNovoAditivo,
            movimentoId: dados?.id,
            funcao: 'Adicionar',
        });
    }, [dados?.id, initialNovoAditivo]);

    // =========================
    // REGRA: AO MUDAR O TIPO
    // =========================
    const applyTipoDefaults = useCallback(
        (tipo) => {
            const aluguelAtual = getAluguelAtual();
            const hoje = moment().format('YYYY-MM-DD');

            // fim do contrato (data final)
            const dataFimContrato = dados?.saida ? moment(dados.saida).startOf('day').format('YYYY-MM-DD') : hoje;

            if (!tipo) {
                limparAditivo();
                return;
            }

            if (tipo === 'Prorrogar') {
                setNovoAditivo((prev) => ({
                    ...prev,
                    tipo,
                    dataAditivo: dataFimContrato,
                    valorAnterior: aluguelAtual,
                    // prorrogar pode OU NÃO ter reajuste, então deixamos editável e sincronizado
                    percentual: 0,
                    valorReajustado: aluguelAtual,
                    // meses: usuário digita
                    quantidade: prev.quantidade ?? 0,
                    funcao: prev.funcao || 'Adicionar',
                }));
                return;
            }

            if (tipo === 'Reajuste') {
                setNovoAditivo((prev) => ({
                    ...prev,
                    tipo,
                    dataAditivo: hoje,
                    valorAnterior: aluguelAtual,
                    percentual: 0,
                    valorReajustado: aluguelAtual,
                    // reajuste NÃO usa meses
                    quantidade: 0,
                    funcao: prev.funcao || 'Adicionar',
                }));
                return;
            }

            // outros tipos (se existirem)
            setNovoAditivo((prev) => ({ ...prev, tipo }));
        },
        [dados?.saida, getAluguelAtual]
    );

    const setRemoteAditivo = useCallback((rows = [], total) => {
        const list = Array.isArray(rows) ? rows : [];
        setItemAditivos(list);
        setRowCountAditivo(Number(total ?? list.length ?? 0));
    }, []);

    const fetchAditivos = useCallback(
        async ({ start = 0, size = 10, filters = '[]', filtersMode = '[]', globalFilter = '', sorting = '[]', movimentoId }) => {
            if (!movimentoId) {
                setRemoteAditivo([], 0);
                return { rows: [], rowCount: 0 };
            }

            setIsFetchingAditivos(true);
            setIsErrorAditivos(false);
            try {
                const resp = await MovimentoAPI.listarAditivos(
                    start,
                    size,
                    filters,
                    filtersMode,
                    globalFilter,
                    sorting,
                    movimentoId
                );
                const rows = resp?.data?.data || [];
                const total = resp?.data?.meta?.totalRowCount ?? rows.length;
                setRemoteAditivo(rows, total);
                return { rows, rowCount: total };
            } catch (error) {
                setIsErrorAditivos(true);
                throw error;
            } finally {
                setIsFetchingAditivos(false);
            }
        },
        [MovimentoAPI, setRemoteAditivo]
    );
    // =========================
    // HANDLER GENÉRICO DO DRAFT
    // =========================
    const handleChangeNovoAditivo = useCallback(
        (name, value) => {
            const field = String(name || '').replace('novoAditivo.', '');

            if (!field) return;

            const camposNumerico = ['percentual', 'quantidade', 'valorReajustado'];

            if (field === 'tipo') {
                applyTipoDefaults(value);
                return;
            }

            // numéricos com máscara
            if (camposNumerico.includes(field)) {
                const valorFormatado = formatarNumeroDecimal(value); // -> "1234.56"

                setNovoAditivo((prev) => {
                    const next = { ...prev, [field]: valorFormatado };

                    // ✅ garante valorAnterior para cálculo (se já tiver no estado)
                    const base = Number(next.valorAnterior || 0);

                    // percentual => recalcula valorReajustado
                    if (field === 'percentual') {
                        if (base > 0) {
                            const perc = Number(valorFormatado || 0) / 100 + 1;
                            next.valorReajustado = (base * perc).toFixed(2);
                        }
                    }

                    // valorReajustado => recalcula percentual
                    if (field === 'valorReajustado') {
                        const vr = Number(valorFormatado || 0);
                        if (base > 0 && vr > 0) {
                            next.percentual = ((vr / base - 1) * 100).toFixed(2);
                        }
                    }

                    return next;
                });

                return;
            }
            // qualquer outro campo (observacao etc)
            setNovoAditivo((prev) => ({ ...prev, [field]: value }));
        },
        [applyTipoDefaults]
    );

    // =========================
    // CRUD (o seu já existente)
    // =========================
    const handleAditivoEditar = useCallback((originalRow, rowIndex) => {
        setNovoAditivo({
            id: originalRow.id,
            uuid: originalRow.uuid,
            empresaId: originalRow.empresaId ?? dados.empresaId,
            movimentoId: originalRow.movimentoId,
            tipo: originalRow.tipo,
            dataAditivo: originalRow.dataAditivo ? moment(originalRow.dataAditivo).format('YYYY-MM-DD') : null,
            valorAnterior: formatarNumeroDecimal(originalRow.valorAnterior ?? 0),
            percentual: formatarNumeroDecimal(originalRow.percentual ?? 0),
            valorReajustado: formatarNumeroDecimal(originalRow.valorReajustado ?? 0),
            quantidade: formatarNumeroDecimal(originalRow.quantidade ?? 0),
            observacao: originalRow.observacao,
            ativo: originalRow.ativo,
            funcao: 'Editar',
            index: rowIndex,
        });
    }, []);

    const handleAditivoDelete = useCallback((originalRow, rowIndex) => {
        setNovoAditivo({
            uuid: originalRow.uuid,
            empresaId: originalRow.empresaId ?? dados.empresaId,
            movimentoId: originalRow.movimentoId,
            tipo: originalRow.tipo,
            dataAditivo: originalRow.dataAditivo ? moment(originalRow.dataAditivo).format('YYYY-MM-DD') : null,
            valorAnterior: formatarNumeroDecimal(originalRow.valorAnterior),
            percentual: formatarNumeroDecimal(originalRow.percentual) ?? 0,
            valorReajustado: formatarNumeroDecimal(originalRow.valorReajustado) ?? 0,
            quantidade: formatarNumeroDecimal(originalRow.quantidade) ?? 0,
            observacao: originalRow.observacao,
            ativo: originalRow.ativo,
            funcao: 'Deletar',
            index: rowIndex,
        });
    }, []);

    const CreateOrEditAditivo = useCallback(
        async (payload) => {
            const base = getAluguelAtual();
            payload.valorAnterior = base ?? payload.valorAnterior ?? null;

            const result = await MovimentoAPI.managerAditivo(payload);

            const msgText = result?.message ? result.message : result?.response?.data?.message;
            const msgType = result?.type ? result.type : result?.response?.data?.type;

            if (msgText) setFlashMessage(msgText, msgType || 'success');
            return result;
        },
        [MovimentoAPI, getAluguelAtual, setFlashMessage]
    );

    const handleAditivo = useCallback(
        async (draft) => {
            if (!dados?.id) return;

            // Reajuste: contrato precisa estar vigente (você pediu essa regra)
            if ((draft?.tipo || '')?.toLowerCase() === 'reajuste') {
                const hoje = moment().startOf('day');
                const entrada = dados?.entrada ? moment(dados.entrada).startOf('day') : null;
                const saida = dados?.saida ? moment(dados.saida).startOf('day') : null;

                if (entrada && saida && !(hoje.isSameOrAfter(entrada) && hoje.isSameOrBefore(saida))) {
                    setFlashMessage('Reajuste só pode ser feito com contrato vigente.', 'warning');
                    return;
                }
            }

            const payload = { ...draft, movimentoId: dados.id };
            if (payload.dataAditivo) {
                payload.dataAditivo = moment(payload.dataAditivo).format('YYYY-MM-DD');
            }

            const funcao = (payload.funcao || '')?.toLowerCase();

            if (funcao === 'editar') {
                if (payload.uuid) {
                    await CreateOrEditAditivo(payload);
                } else {
                    setItemAditivos((prev) => {
                        const next = [...prev];
                        next[payload.index] = { ...next[payload.index], ...payload };
                        return next;
                    });
                }
            } else if (funcao === 'deletar') {
                if (payload.uuid) {
                    const response = await MovimentoAPI.deletarAditivo(payload.uuid);
                    setFlashMessage(response.data.message, response.data.type);
                } else {
                    setItemAditivos((prev) => {
                        const next = [...prev];
                        next.splice(payload.index, 1);
                        return next;
                    });
                }
            } else {
                const response = await MovimentoAPI.duplicidadeAditivo(payload.movimentoId, payload.tipo, payload.dataAditivo);

                if (response?.status === 422) {
                    setFlashMessage(response.data.message, response.data.type);
                    return;
                }
                if (response) {
                    setFlashMessage('Registo em duplicidade!', 'warning');
                    handleAditivoEditar(response, payload.index);
                    return;
                }
                await CreateOrEditAditivo(payload);
            }

            limparAditivo();
            if (fetchData) await fetchData('aditivo');
        },
        [
            dados?.id,
            dados?.entrada,
            dados?.saida,
            MovimentoAPI,
            setFlashMessage,
            CreateOrEditAditivo,
            handleAditivoEditar,
            limparAditivo,
            fetchData,
        ]
    );

    const imprimirDocumento = useCallback(
        async (documento, rowOriginal) => {
            try {
                const caminho = documento?.Documento?.caminho;
                const url = documento?.Documento?.funcao;

                if (!caminho || !url) {
                    setFlashMessage('Documento não configurado corretamente (caminho/funcao).', 'warning');
                    return;
                }
                const obj = {
                    ...rowOriginal,
                    empresaId: dados.empresaId, //empresaId é obrigatório
                };
                const response = await relatorios(caminho, url, rowOriginal);

                printPdf(response.data);
            } catch (error) {
                console.error(error);
                const msg = error?.response?.data?.message || 'Erro ao gerar/imprimir documento.';
                setFlashMessage(msg, 'error');
            }
        },
        [setFlashMessage]
    );

    const main = {
        state: dados,
    };

    const draft = {
        value: novoAditivo,
        setValue: setNovoAditivo,
        clear: limparAditivo,
        selectRow: handleAditivoEditar,
    };

    const list = {
        items: itemAditivos,
        rowCount: rowCountAditivo,
        setItems: setItemAditivos,
        setRowCount: setRowCountAditivo,
    };

    const mrt = {
        raw: mrtState,
        state: {
            columnFilterFns: mrtState?.columnFilterFns ?? {},
            columnFilters: mrtState?.columnFilters ?? [],
            globalFilter: mrtState?.globalFilter ?? '',
            pagination: mrtState?.pagination ?? { pageIndex: 0, pageSize: 10 },
            sorting: mrtState?.sorting ?? [],
        },
        setters: {
            setColumnFilterFnsMode: mrtState?.setColumnFilterFnsMode,
            setColumnFilters: mrtState?.setColumnFilters,
            setGlobalFilter: mrtState?.setGlobalFilter,
            setPagination: mrtState?.setPagination,
            setSorting: mrtState?.setSorting,
        },
        flags: {
            isLoading: isFetchingAditivos && itemAditivos.length === 0,
            isRefetching: isFetchingAditivos && itemAditivos.length > 0,
            isError: isErrorAditivos,
        },
    };

    const flags = {
        isSaving: saveLock.isLocked,
        isRefreshing: refreshLock.isLocked,
        isPrinting: printLock.isLocked,
    };

    const actions = {
        change: (eOrName, value) => {
            if (typeof eOrName === 'string') {
                handleChangeNovoAditivo(eOrName, value);
                return;
            }

            const e = eOrName;
            const { name, value: nextValue } = e?.target || {};
            handleChangeNovoAditivo(name, nextValue);
        },

        focus: async () => {},
        blur: async () => {},

        refresh: async (params = {}) =>
            await refreshLock.run(async () => {
                return await fetchAditivos({
                    movimentoId: dados?.id,
                    ...params,
                });
            }),

        submitDraft: async (draftValue = novoAditivo) =>
            await saveLock.run(async () => {
                return await handleAditivo(draftValue);
            }),

        selectRow: handleAditivoEditar,

        remove: async (rowOriginal, rowIndex) =>
            await saveLock.run(async () => {
                handleAditivoDelete(rowOriginal, rowIndex);
                return await handleAditivo({
                    ...rowOriginal,
                    funcao: 'Deletar',
                    index: rowIndex,
                });
            }),

        clear: limparAditivo,

        print: async (documento, rowOriginal) =>
            await printLock.run(async () => {
                return await imprimirDocumento(documento, rowOriginal);
            }),

        setRemote: setRemoteAditivo,
    };

    const state = {
        novoAditivo,
        setNovoAditivo,
        itemAditivos,
        setItemAditivos,
        rowCountAditivo,
        setRowCountAditivo,
    };

    return {
        key: 'aditivo',

        main,
        draft,
        list,
        mrt,
        flags,
        actions,

        services: {
            fetchAditivos,
            setRemoteAditivo,
            limparAditivo,
            handleAditivo,
            handleAditivoEditar,
            handleAditivoDelete,
            imprimirDocumento,
            handleChangeNovoAditivo,
            applyTipoDefaults,
            getAluguelAtual,
        },

        state, // compat temporária
    };
};
